import { useCallback, useEffect, useRef, useState } from 'react';
import { formatDate, formatMoney, type RequestStatus } from '@pr-app/shared';
import {
  Body1,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Link,
  Menu,
  MenuDivider,
  MenuItem,
  MenuItemLink,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Skeleton,
  SkeletonItem,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { useNavigate } from 'react-router-dom';
import {
  deleteDraft,
  duplicateRequest,
  listRequests,
  saveDraft,
  withdrawRequest,
  type DashboardDateRange,
  type DashboardSortField,
  type DashboardSortOrder,
  type ListRequestsResult,
  type RequestSummary,
} from '../api/requests';
import { fromDateOnly, toDateOnly } from '../lib/dateOnly';
import { useVendorNames, vendorLabel } from '../lib/useVendorNames';
import { getAdminDashboard } from '../api/admin';
import { getReviewDashboard } from '../api/review';
import { useResponsiveStyles } from '../hooks/useResponsiveStyles';
import { useRestoreFocusOnClose } from '../hooks/useRestoreFocusOnClose';
import { SPACE, SPACE_X } from '../styles/scale';
import { useA11yStyles, useLayoutStyles, useSurfaceStyles } from '../styles/surfaces';
import { ApiErrorMessage } from '../components/ApiErrorMessage';
import { DataTable, type Column } from '../components/DataTable';
import { EmptyState } from '../components/EmptyState';
import { FilterBar } from '../components/FilterBar';
import { PageHeader } from '../components/PageHeader';
import { StatTile } from '../components/StatTile';
import { StatusBadge } from '../components/StatusBadge';

/**
 * SOW §4.1 — My Requests: status counters (Total / Pending review / PR created /
 * Rejected), searchable & filterable list, sort, paging, and the New Request
 * button (wireframe sheet 01, frames R1/R1a/R1b/R1c/R1d — #152).
 *
 * Draft list, resume, delete and expiry (#33, wireframes R7a/R7b/D2) live
 * here too — drafts sit in the same table as everything else, badged Draft,
 * rather than on a separate screen.
 *
 * The list below is the first live call the tab makes, so it doubles as the
 * end-to-end proof that Teams SSO works: `api()` fetches the SSO token and the
 * server validates it. A failure is surfaced rather than swallowed — a silently
 * empty list is indistinguishable from a broken sign-in.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * #202 — THE PAGE IS NOW ASSEMBLED FROM THE SHARED PRIMITIVES
 * ─────────────────────────────────────────────────────────────────────────────
 * Every block that used to be hand-styled here is a primitive now, and each one
 * fixes something measured on this exact screen:
 *
 *  - `PageHeader` — the page had NO `<h1>`; its title was a `<Title2>`, which
 *    renders a `<span>`. Visually a heading, and ordinary text to a screen
 *    reader (and 28/36 is the stat-tile size, so it also shouted a step too
 *    loud).
 *  - `StatTile` — the selected counter marked itself with brand colour and
 *    nothing else, so under `teamsHighContrastTheme` (which maps every
 *    `colorBrand*` onto a Windows system colour) the selected tile was
 *    indistinguishable from the other three. The tile also fell back to the
 *    browser's `buttontext` colour and rendered blank in high contrast
 *    (CLAUDE.md log entry 54).
 *  - `DataTable` — **the clipping bug.** Measured in a real browser: at 1440px
 *    the table wanted 1430px of content inside a 1375px box, and
 *    `surfaces.tableCard`'s `overflow: hidden` cut the Actions column off with
 *    no scrollbar anywhere (the document reported ZERO horizontal overflow, so
 *    nobody ever saw one). At 900px — a Teams tab with the side chat open —
 *    115px went missing and the row menu was literally unreachable. `DataTable`
 *    owns a dedicated scroll element inside the card, so content is reachable
 *    at every viewport by construction.
 *  - `FilterBar` — owns the control widths and the clear affordance.
 *  - `EmptyState` — the three "nothing here" states were three different
 *    hand-rolled frames; they are three distinct variants now (sheet 10.2).
 *  - `useLayoutStyles().stack` — vertical rhythm is ONE gap, owned by the page,
 *    replacing the eight separate `marginTop: '16px'` rules that used to
 *    compound with whatever gap their parent already had.
 */

type LoadState =
  | { phase: 'loading' }
  | { phase: 'ready'; result: ListRequestsResult }
  | { phase: 'error'; error: unknown };

/** Wireframe R7b — warn once a draft is this close to being removed for inactivity. */
const EXPIRY_WARNING_DAYS = 7;

/** Wireframe R1: "Showing 5 of 24" — the dashboard's own default page size. */
const PAGE_SIZE = 5;
/** Wireframe R1 note 6 — "The 90-day default keeps the first paint small." */
const DEFAULT_RANGE: DashboardDateRange = 'last90';
const SEARCH_DEBOUNCE_MS = 300;

const STATUS_OPTIONS: RequestStatus[] = [
  'DRAFT',
  'PENDING_REVIEW',
  'REJECTED',
  'APPROVED',
  'PR_CREATED',
];
const STATUS_OPTION_LABELS: Record<RequestStatus, string> = {
  DRAFT: 'Draft',
  PENDING_REVIEW: 'Pending review',
  REJECTED: 'Rejected',
  APPROVED: 'Approved',
  PR_CREATED: 'PR created',
};

type SortChoice = `${DashboardSortField}-${DashboardSortOrder}`;
const SORT_CHOICES: { value: SortChoice; label: string }[] = [
  { value: 'updatedAt-desc', label: 'Newest updated' },
  { value: 'updatedAt-asc', label: 'Oldest updated' },
  { value: 'requestNo-desc', label: 'Request # (highest first)' },
  { value: 'requestNo-asc', label: 'Request # (lowest first)' },
  { value: 'total-desc', label: 'Total (highest first)' },
  { value: 'total-asc', label: 'Total (lowest first)' },
];

const RANGE_CHOICES: { value: DashboardDateRange; label: string }[] = [
  { value: 'last30', label: 'Last 30 days' },
  { value: 'last90', label: 'Last 90 days' },
  { value: 'year', label: 'This year' },
  { value: 'custom', label: 'Custom range' },
];

interface Filters {
  q: string;
  status: RequestStatus[];
  company: string[];
  range: DashboardDateRange;
  from: string;
  to: string;
  sort: DashboardSortField;
  order: DashboardSortOrder;
  page: number;
}

const DEFAULT_FILTERS: Filters = {
  q: '',
  status: [],
  company: [],
  range: DEFAULT_RANGE,
  from: '',
  to: '',
  sort: 'updatedAt',
  order: 'desc',
  page: 1,
};

/**
 * The three sortable column keys are spelled as the API's own sort fields, so
 * `DataTable`'s `onSort(key)` needs a narrowing rather than a lookup table —
 * one name for one concept, and a column key that drifts from a sort field
 * stops compiling rather than silently sorting by nothing.
 */
function isSortField(key: string): key is DashboardSortField {
  return key === 'requestNo' || key === 'total' || key === 'updatedAt';
}

function typeLabel(purchaseType: string | null): string {
  if (purchaseType === 'CAPITAL') return 'Capital';
  if (purchaseType === 'REGULAR') return 'Regular';
  return '—';
}

function totalText(request: RequestSummary): string {
  return request.currencyCode
    ? `${request.currencyCode} ${formatMoney(request.total, request.currencyCode)}`
    : '—';
}

const useStyles = makeStyles({
  /**
   * The counter row is a `<fieldset>` so its `<legend>` can name the group
   * ("Filter by status") without a heading nobody asked for on screen — jsx-a11y
   * prefers a real fieldset/legend pair over a bare `role="group"`. A fieldset
   * carries a user-agent border, padding and margin, and a `min-inline-size` of
   * `min-content` that would fight the grid `tileRow` puts on it; all four are
   * reset here and the tiles draw their own frames.
   */
  tileGroup: {
    border: 'none',
    padding: SPACE.none,
    margin: SPACE.none,
    minInlineSize: '0',
  },

  /**
   * The Request # cell: the row's own keyboard/screen-reader target stacked
   * over the request's title. `<span>` rather than `<div>`, because on the
   * mobile card `DataTable` places a column's rendered value inside a `<span>`,
   * and a block element nested in an inline one is markup React will not warn
   * about and a browser will lay out unpredictably.
   */
  primaryCell: {
    display: 'flex',
    flexDirection: 'column',
    // A flex column ignores the cell's own `text-align: center` (#211) —
    // `alignItems` is the cross-axis equivalent for a column, so it is set
    // here to match the desktop table's centered cells.
    alignItems: 'center',
    textAlign: 'center',
    gap: SPACE.xs,
    // Below the breakpoint this same render is a card FIELD, and `DataTable`
    // end-aligns every field value against its label. A flex column ignores the
    // inherited `text-align`, so without this the one multi-line value on the
    // card is the only one hanging off the left edge (seen at 390px).
    '@media (max-width: 700px)': {
      alignItems: 'flex-end',
      textAlign: 'end',
    },
  },

  /** De-emphasis is a foreground token, never `opacity`. */
  meta: { color: tokens.colorNeutralForeground2 },

  /** A cell whose content is a fixed-height pill and must never be broken over two lines. */
  nowrapCell: { whiteSpace: 'nowrap' },

  /**
   * Buttons within one action cluster (#211). Stacked column-wise rather than
   * side-by-side: a DRAFT row's "Resume" + "Actions" pair, laid out in a row,
   * was what forced the Actions cell wider than its column and pushed the
   * whole table into horizontal overflow on a real screenshot. Stacking keeps
   * every action reachable inside the cell's own width instead of widening
   * it. The `s` step is still the right gap — it is the same "buttons within
   * one action cluster" use named in the spacing scale, just on the vertical
   * axis now.
   */
  actionCluster: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: SPACE.s,
  },

  /**
   * The delete-draft confirm. `appearance="primary"` on a destroy action is
   * banned: with a saturated indigo primary, the loudest, most inviting thing
   * on "Delete this draft?" would be the button that deletes it. Emphasis moves
   * off the brand instead — a `secondary` button wearing the red palette, with
   * "Keep draft" dropping to `subtle` beside it. The pair still reads correctly
   * with no colour at all (high contrast discards the palette exactly as it
   * discards the brand hue), because the labels carry the meaning.
   *
   * `border`, NOT `borderColor`: Griffel rejects `borderColor` as an
   * unsupported shorthand and DROPS the rule, so a red-bordered button written
   * that way is silently a grey-bordered one. The hover/active states are
   * restated because Fluent's own `:hover` pulls the colour back to neutral
   * mid-gesture otherwise. Same treatment `ActionBar` gives its own destructive
   * confirm — this dialog cannot use `ActionBar` itself because K1 requires a
   * `ref` on "Keep draft" (focus lands on the non-destructive action), and
   * `ActionBar`'s `back` slot is a `{ label, onClick }` object with nowhere to
   * put one.
   */
  destructiveConfirm: {
    color: tokens.colorPaletteRedForeground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorPaletteRedBorder2}`,
    ':hover': {
      color: tokens.colorPaletteRedForeground1,
      border: `${tokens.strokeWidthThin} solid ${tokens.colorPaletteRedBorder2}`,
    },
    ':hover:active': {
      color: tokens.colorPaletteRedForeground1,
      border: `${tokens.strokeWidthThin} solid ${tokens.colorPaletteRedBorder2}`,
    },
  },

  /**
   * The loading region. `<output>` is inline by default and a native form
   * element, so it needs both a block box and an explicit foreground token —
   * neither is inherited from `FluentProvider` (CLAUDE.md log entry 54).
   */
  loading: {
    display: 'block',
    color: tokens.colorNeutralForeground1,
  },

  /** A skeleton tile: the number's line over the label's, inside the shared card frame. */
  skeletonTile: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.s,
  },

  /** `tableCard` draws no padding of its own — the real table's cells supply it. */
  skeletonTable: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.l,
    padding: SPACE.l,
  },

  /** Pagination buttons, as one cluster at the trailing end of `surfaces.pager`. */
  pagerButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE_X.s,
  },
});

/** Wireframe R7a's own example is "Server rack expansion" / "Untitled" as a plain line. */
function draftMetaLine(request: RequestSummary): string {
  const expiry =
    request.daysUntilExpiry !== null && request.daysUntilExpiry <= EXPIRY_WARNING_DAYS
      ? `Expires in ${String(request.daysUntilExpiry)} day${request.daysUntilExpiry === 1 ? '' : 's'}`
      : `Last edited ${formatDate(request.updatedAt)}`;
  return `${request.completeness} · ${expiry}`;
}

/**
 * Wireframe R1b — loading is a SKELETON, not a spinner. The shape of this page
 * is already known (four counters, a filter bar, a list), so holding that shape
 * stops every block jumping down the moment data lands. The skeleton itself is
 * `aria-hidden`: it means nothing to a screen reader, and the one sentence that
 * does is the visually-hidden line inside the `status` region.
 *
 * `<output>` rather than a `<div role="status">`: it carries that role natively
 * — implicitly polite, so the update settles rather than interrupting — and it
 * is the element `FilterBar` already uses for its own in-flight line. Being a
 * native form element it does NOT inherit `color` from `FluentProvider`, hence
 * the explicit token (CLAUDE.md log entry 54).
 */
function DashboardSkeleton() {
  const styles = useStyles();
  const surfaces = useSurfaceStyles();
  const layout = useLayoutStyles();
  const a11y = useA11yStyles();

  return (
    <output className={styles.loading}>
      <span className={a11y.visuallyHidden}>Loading your requests…</span>
      <Skeleton aria-hidden="true" className={layout.stack}>
        <div className={layout.tileRow}>
          {[0, 1, 2, 3].map((tile) => (
            <div key={tile} className={mergeClasses(surfaces.card, styles.skeletonTile)}>
              <SkeletonItem size={28} />
              <SkeletonItem size={12} />
            </div>
          ))}
        </div>
        <div className={mergeClasses(surfaces.tableCard, styles.skeletonTable)}>
          {[0, 1, 2, 3, 4].map((row) => (
            <SkeletonItem key={row} size={16} />
          ))}
        </div>
      </Skeleton>
    </output>
  );
}

export interface DashboardProps {
  /**
   * `'mine'` (default) — the requester's own submitted requests, exactly as
   * before #222. `'review'` — a reviewer's role-aware view: the pending queue
   * plus every request the viewer has personally approved or rejected, never
   * an edit surface (SOW: a reviewer only ever acts through Reviewer Queue's
   * own approve/reject flow). `'admin'` (#224) — every non-draft request
   * across the whole org, purely for oversight: broader than `'review'` (no
   * owner/actor scoping at all) and with even less action capability (not
   * even approve/reject — that stays reviewer-only). Defaults to `'mine'` so
   * every existing caller — and every existing test — keeps today's
   * behaviour unchanged.
   */
  mode?: 'mine' | 'review' | 'admin';
}

export function Dashboard({ mode = 'mine' }: DashboardProps = {}) {
  const styles = useStyles();
  const surfaces = useSurfaceStyles();
  const layout = useLayoutStyles();
  const a11y = useA11yStyles();
  const responsive = useResponsiveStyles();
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [deleteTarget, setDeleteTarget] = useState<RequestSummary | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [keepDraftBusyId, setKeepDraftBusyId] = useState<string | null>(null);
  const [withdrawTarget, setWithdrawTarget] = useState<RequestSummary | null>(null);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [searchInput, setSearchInput] = useState('');

  // K1 "Dialog closes" — back to the row's Actions button, which is where
  // Fluent leaves focus after the menu holding "Delete draft" closes.
  useRestoreFocusOnClose(deleteTarget !== null);
  // Same K1 requirement for the withdraw-confirmation dialog (#149).
  useRestoreFocusOnClose(withdrawTarget !== null);
  // K1 "Dialog opens" — focus the non-destructive action, the same choice
  // RequestForm's close-warning dialog makes: "Delete draft" is the one
  // button here that can lose data, so "Keep draft" gets initial focus
  // rather than Fluent's first-focusable default (which happens to already
  // be "Keep draft", but under jsdom nothing is found and focus falls to the
  // surface instead — log entry 21).
  const keepDraftFocusRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (deleteTarget !== null) keepDraftFocusRef.current?.focus();
  }, [deleteTarget]);
  // Same choice for withdraw: "Keep in review" is the non-destructive action.
  const keepInReviewFocusRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (withdrawTarget !== null) keepInReviewFocusRef.current?.focus();
  }, [withdrawTarget]);

  // Search box (wireframe R1 note 3) — debounced ~300ms, server-side. Typing
  // never blanks the table; the previous result stays until the new one lands.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((f) => (f.q === searchInput ? f : { ...f, q: searchInput, page: 1 }));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Selecting "Custom range" from the dropdown lands on this branch before
  // either date is picked — the API correctly 400s on `range=custom` with no
  // `from`/`to`, so don't fire that request at all; wait for both dates.
  const isFetchable = useCallback(
    (params: Filters) => params.range !== 'custom' || (params.from !== '' && params.to !== ''),
    [],
  );

  const load = useCallback(
    async (params: Filters, signal?: AbortSignal) => {
      setState({ phase: 'loading' });
      try {
        const query = {
          q: params.q || undefined,
          status: params.status.length > 0 ? params.status : undefined,
          company: params.company.length > 0 ? params.company : undefined,
          range: params.range,
          from: params.range === 'custom' ? params.from || undefined : undefined,
          to: params.range === 'custom' ? params.to || undefined : undefined,
          sort: params.sort,
          order: params.order,
          page: params.page,
          pageSize: PAGE_SIZE,
        };
        // #222/#224 — same request/response shape across all three, so
        // nothing past this point needs to know which endpoint answered it.
        const result =
          mode === 'admin'
            ? await getAdminDashboard(query)
            : mode === 'review'
              ? await getReviewDashboard(query)
              : await listRequests(query);
        if (signal?.aborted) return;
        setState({ phase: 'ready', result });
      } catch (err) {
        if (signal?.aborted) return;
        setState({ phase: 'error', error: err });
      }
    },
    [mode],
  );

  useEffect(() => {
    if (!isFetchable(filters)) return;
    // Guards against setting state after unmount, which React 19 in StrictMode
    // makes easy to hit by mounting every effect twice.
    const controller = new AbortController();
    void load(filters, controller.signal);
    return () => controller.abort();
  }, [load, filters, isFetchable]);

  const reload = useCallback(() => {
    if (!isFetchable(filters)) return Promise.resolve();
    return load(filters);
  }, [load, filters, isFetchable]);

  const requests = state.phase === 'ready' ? state.result.requests : [];
  const vendorNames = useVendorNames(requests);
  const drafts = requests.filter((r) => r.status === 'DRAFT');
  const expiringDrafts = drafts.filter(
    (r) => r.daysUntilExpiry !== null && r.daysUntilExpiry <= EXPIRY_WARNING_DAYS,
  );

  function updateFilters(patch: Partial<Omit<Filters, 'page'>>) {
    setFilters((f) => ({ ...f, ...patch, page: 1 }));
  }

  function clearFilters() {
    setSearchInput('');
    // A fresh object, not the shared `DEFAULT_FILTERS` reference — setting
    // state to the exact same object React already holds is a no-op (React
    // bails out via `Object.is`), so if the filters were already at their
    // defaults this would silently refuse to refetch.
    setFilters({ ...DEFAULT_FILTERS });
  }

  /** Wireframe R1 note 2 — the counters are filters, not decoration. Selecting
   * one applies that status filter; selecting it again clears back to "Total". */
  function toggleCounter(status: RequestStatus | 'ALL') {
    if (status === 'ALL') {
      updateFilters({ status: [] });
      return;
    }
    const isOnlySelected = filters.status.length === 1 && filters.status[0] === status;
    updateFilters({ status: isOnlySelected ? [] : [status] });
  }

  async function handleKeepDraft(id: string) {
    setKeepDraftBusyId(id);
    try {
      // Re-saving with nothing but the id is a no-op update that still bumps
      // `updatedAt` — the same "touch" the auto-save endpoint already does on
      // every real edit, just triggered manually here to reset the clock.
      await saveDraft({ id });
      await reload();
    } finally {
      setKeepDraftBusyId(null);
    }
  }

  async function handleConfirmDelete() {
    if (!deleteTarget) return;
    setDeleteError(null);
    try {
      await deleteDraft(deleteTarget.id);
      setDeleteTarget(null);
      await reload();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : String(err));
    }
  }

  async function handleConfirmWithdraw() {
    if (!withdrawTarget) return;
    setWithdrawError(null);
    try {
      await withdrawRequest(withdrawTarget.id);
      setWithdrawTarget(null);
      await reload();
    } catch (err) {
      setWithdrawError(err instanceof Error ? err.message : String(err));
    }
  }

  async function handleDuplicate(id: string) {
    setActionError(null);
    try {
      const copy = await duplicateRequest(id);
      void navigate(`/requests/${copy.id}`);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    }
  }

  // Wireframe R1 note 7 — a Draft opens the form for editing, every other
  // status opens read-only detail (RequestForm renders read-only itself once a
  // request leaves DRAFT/REJECTED — #144), Rejected opens with the edit banner.
  function openRequest(id: string) {
    void navigate(`/requests/${id}`);
  }

  // Wireframe R1 note 9 — Request #, Total and Updated are sortable from the
  // table's own header, alongside the existing "Sort by" dropdown (#152) —
  // both drive the same `filters.sort`/`filters.order` state. A second click
  // on the already-active column flips direction; a different column starts
  // descending, matching the dropdown's own default choices.
  function handleSortClick(key: string) {
    if (!isSortField(key)) return;
    setFilters((f) => ({
      ...f,
      sort: key,
      order: f.sort === key && f.order === 'desc' ? 'asc' : 'desc',
      page: 1,
    }));
  }

  // #222 — a reviewer's Dashboard is read-only: no Resume/Duplicate/Delete/
  // Withdraw anywhere (all of those would 404 server-side for a non-owner
  // anyway — `assertCanModifyRequest` is owner-only). The only actions are
  // "Review" (opens the existing approve/reject flow in ReviewerQueue,
  // never a new one here) and "View" — reusing `openRequest` exactly as the
  // requester's own dashboard does.
  function renderReviewRowActions(request: RequestSummary) {
    if (request.status === 'PENDING_REVIEW') {
      return (
        <div className={styles.actionCluster}>
          <Button
            appearance="secondary"
            disabled={request.isOwn}
            title={request.isOwn ? 'You cannot review your own request' : undefined}
            aria-label={
              request.isOwn
                ? `Review PR-${request.requestNo} — you cannot review your own request`
                : `Review PR-${request.requestNo}`
            }
            onClick={() => {
              void navigate(`/review?open=${encodeURIComponent(request.id)}`);
            }}
          >
            Review
          </Button>
        </div>
      );
    }

    return (
      <div className={styles.actionCluster}>
        <Button appearance="secondary" onClick={() => openRequest(request.id)}>
          View
        </Button>
      </div>
    );
  }

  // #224 — an admin's Dashboard is oversight-only: every row, regardless of
  // status (including PENDING_REVIEW), gets a single "View" action and
  // nothing else. Unlike a reviewer, an admin has no "Review" entry point
  // anywhere on this screen — approve/reject stays reachable only through
  // Reviewer Queue, by someone who is actually a reviewer.
  function renderAdminRowActions(request: RequestSummary) {
    return (
      <div className={styles.actionCluster}>
        <Button appearance="secondary" onClick={() => openRequest(request.id)}>
          View
        </Button>
      </div>
    );
  }

  // The row's Actions overflow menu (wireframe R1d), shared verbatim between
  // the desktop table and the narrow-viewport card — `DataTable` renders it in
  // both faces and stops a menu click from also firing the row's navigation.
  function renderRowActions(request: RequestSummary) {
    if (mode === 'admin') return renderAdminRowActions(request);
    if (mode === 'review') return renderReviewRowActions(request);

    if (request.status === 'DRAFT') {
      return (
        <div className={styles.actionCluster}>
          <Button
            appearance="secondary"
            onClick={() => {
              void navigate(`/requests/${request.id}`);
            }}
          >
            Resume
          </Button>
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button appearance="transparent" aria-label={`Actions for PR-${request.requestNo}`}>
                Actions
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={() => openRequest(request.id)}>Continue editing</MenuItem>
                <MenuItem onClick={() => void handleDuplicate(request.id)}>
                  Duplicate as new request
                </MenuItem>
                <MenuDivider />
                <MenuItem onClick={() => setDeleteTarget(request)}>Delete draft</MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      );
    }

    // #149 — pulling a submitted request back out of review, wireframe 08.3
    // behaviour rule 2, D2 — alongside View request and Duplicate (R1d — #152).
    if (request.status === 'PENDING_REVIEW') {
      return (
        <div className={styles.actionCluster}>
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button appearance="transparent" aria-label={`Actions for PR-${request.requestNo}`}>
                Actions
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={() => openRequest(request.id)}>View request</MenuItem>
                <MenuItem onClick={() => void handleDuplicate(request.id)}>
                  Duplicate as new request
                </MenuItem>
                <MenuDivider />
                <MenuItem onClick={() => setWithdrawTarget(request)}>Withdraw from review</MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      );
    }

    // Wireframe R1d note 5 — nothing can be done while the requisition is
    // being created; View request only (#152).
    if (request.status === 'APPROVED') {
      return (
        <div className={styles.actionCluster}>
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button appearance="transparent" aria-label={`Actions for PR-${request.requestNo}`}>
                Actions
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={() => openRequest(request.id)}>View request</MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      );
    }

    // Wireframe R1d — View request, Open PR in D365, Duplicate (#152).
    if (request.status === 'PR_CREATED') {
      return (
        <div className={styles.actionCluster}>
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button appearance="transparent" aria-label={`Actions for PR-${request.requestNo}`}>
                Actions
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={() => openRequest(request.id)}>View request</MenuItem>
                {request.d365Link && (
                  <MenuItemLink
                    // Leaves Teams — deliberate, and the only such link in
                    // the app (wireframe R1d note 4).
                    href={request.d365Link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open PR in D365 ↗
                  </MenuItemLink>
                )}
                <MenuItem onClick={() => void handleDuplicate(request.id)}>
                  Duplicate as new request
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      );
    }

    // #42 — a rejection is only actionable through the form itself.
    if (request.status === 'REJECTED') {
      return (
        <div className={styles.actionCluster}>
          <Button
            appearance="secondary"
            onClick={() => {
              void navigate(`/requests/${request.id}`);
            }}
          >
            View rejection
          </Button>
        </div>
      );
    }

    return null;
  }

  /**
   * Wireframe R1's columns, declared ONCE — `DataTable` derives the desktop
   * table and the sub-700px cards from this same array, so a column cannot
   * exist on one face and be missing from the other (which is exactly how the
   * old hand-written card meta lines drifted from the table's cells).
   *
   * `priority` is the mobile card's reading ORDER, not a drop list: the card is
   * where space is genuinely finite, so status, total and the request's own
   * identity lead. Nothing is ever removed — a user who cannot see the total is
   * no better off for the fact that we meant to hide it.
   *
   * The one `cardOnly` column carries what the old card said and the desktop
   * table has no column for: the line count.
   */
  const columns: Column<RequestSummary>[] = [
    {
      key: 'requestNo',
      header: 'Request #',
      sortable: true,
      priority: 90,
      // The mobile card is already headed "PR-151"; repeating it as a
      // "Request # · PR-151" field beneath reads as a rendering fault.
      tableOnly: true,
      render: (request) => (
        <span className={styles.primaryCell}>
          {/* The row's own keyboard/screen-reader target — a real control, not
              a bare onClick on the row (wireframe sheet 10, K1). `DataTable`'s
              own `onRowClick` is the same navigation, for pointer users
              clicking anywhere else in the row. */}
          <Link
            as="button"
            onClick={(event) => {
              event.stopPropagation();
              openRequest(request.id);
            }}
          >
            PR-{request.requestNo}
          </Link>
          {request.title && <Caption1 className={styles.meta}>{request.title}</Caption1>}
          {/* Wireframe R7a — completeness and the expiry clock belong to the
              draft itself, so they sit under its number rather than in a column
              of their own. A "Draft progress" column would have to render an em
              dash on the four statuses that have no such concept, which is
              noise on every card that is not a draft. */}
          {request.status === 'DRAFT' && (
            <Caption1 className={styles.meta}>{draftMetaLine(request)}</Caption1>
          )}
        </span>
      ),
    },
    {
      key: 'summary',
      header: 'What it is for',
      priority: 95,
      // The card's counterpart to `requestNo` above. That column is `tableOnly`
      // because the card's heading already carries the request number — but the
      // title and the draft's completeness line live in it too, and those the
      // card still needs. Splitting them means the number appears once (in the
      // heading, as the control that opens the request) and the description
      // once, instead of the whole block twice.
      cardOnly: true,
      render: (request) => (
        <span className={styles.primaryCell}>
          {request.title && <Caption1 className={styles.meta}>{request.title}</Caption1>}
          {request.status === 'DRAFT' && (
            <Caption1 className={styles.meta}>{draftMetaLine(request)}</Caption1>
          )}
        </span>
      ),
    },
    // #224 — an admin is looking at everyone else's requests, so who raised
    // it is a column here, not something only found by drilling in. Admin
    // mode only: every other mode's own row is already the viewer's (or, for
    // a reviewer, either the shared pending queue or something the viewer
    // personally decided), so this would either be redundant or noise there.
    ...(mode === 'admin'
      ? [
          {
            key: 'requester',
            header: 'Requester',
            priority: 65,
            render: (request: RequestSummary) => (
              <span className={styles.primaryCell}>
                <Body1>{request.requesterName}</Body1>
                <Caption1 className={styles.meta}>{request.requesterEmail}</Caption1>
              </span>
            ),
          } satisfies Column<RequestSummary>,
        ]
      : []),
    {
      key: 'vendor',
      header: 'Vendor',
      priority: 60,
      render: (request) => vendorLabel(vendorNames, request),
    },
    {
      key: 'company',
      header: 'Company',
      priority: 50,
      render: (request) => request.companyCode ?? '—',
    },
    {
      key: 'type',
      header: 'Type',
      priority: 30,
      render: (request) => typeLabel(request.purchaseType),
    },
    {
      key: 'total',
      header: 'Total',
      // Wireframe X1 — money is right-aligned with tabular figures, header and
      // cells together, so a column of amounts compares by eye.
      numeric: true,
      sortable: true,
      priority: 95,
      render: (request) => totalText(request),
    },
    {
      key: 'status',
      header: 'Status',
      priority: 100,
      render: (request) => (
        // Measured at 900px — a Teams tab with the side chat open. The table is
        // at its min-content width there, and min-content for a wrapping cell
        // is its longest WORD: the browser broke "Pending review" over two
        // lines, and `Badge` has a fixed height, so the second line rendered
        // straight through the pill's own border and read as struck through.
        // Pinning the cell stops the break and simply makes the table wider,
        // which the scroll element inside the card now absorbs.
        <span className={styles.nowrapCell}>
          <StatusBadge status={request.status} />
        </span>
      ),
    },
    // #224 — the other half of "who touched this request": what the
    // requester column names, this names for whoever approved or rejected
    // it. Em dash while still pending, same convention every other "nothing
    // here yet" cell in this table already uses.
    ...(mode === 'admin'
      ? [
          {
            key: 'decidedBy',
            header: 'Decided by',
            priority: 45,
            render: (request: RequestSummary) =>
              request.decisionAction && request.decidedByName
                ? `${request.decisionAction === 'APPROVED' ? 'Approved' : 'Rejected'} by ${request.decidedByName}`
                : '—',
          } satisfies Column<RequestSummary>,
        ]
      : []),
    {
      key: 'd365Pr',
      header: 'D365 PR',
      priority: 40,
      render: (request) => request.d365PrNumber ?? '—',
    },
    {
      key: 'updatedAt',
      header: 'Updated',
      sortable: true,
      priority: 70,
      render: (request) => formatDate(request.updatedAt),
    },
    {
      key: 'lines',
      header: 'Line items',
      cardOnly: true,
      numeric: true,
      priority: 20,
      render: (request) => request._count.lines,
    },
  ];

  const counts = state.phase === 'ready' ? state.result.counts : null;
  const companies = state.phase === 'ready' ? state.result.companies : [];
  const hasAnyRequestsEver = counts !== null && counts.total > 0;
  const isFirstRun = state.phase === 'ready' && !hasAnyRequestsEver;
  const hasNoMatches = state.phase === 'ready' && hasAnyRequestsEver && requests.length === 0;

  const sortChoice: SortChoice = `${filters.sort}-${filters.order}`;
  const total = state.phase === 'ready' ? state.result.total : 0;
  const pageStart = total === 0 ? 0 : (filters.page - 1) * PAGE_SIZE + 1;
  // Based on how many rows this page actually returned, not an assumption
  // that every page is full — the last page is usually shorter.
  const pageEnd = total === 0 ? 0 : pageStart + requests.length - 1;

  /** How many filters are actually narrowing the list — the date range only
   * counts once it has been moved off its own 90-day default. */
  const activeFilterCount =
    (filters.q === '' ? 0 : 1) +
    (filters.status.length > 0 ? 1 : 0) +
    (filters.company.length > 0 ? 1 : 0) +
    (filters.range === DEFAULT_RANGE ? 0 : 1);

  return (
    // One stack owns the 16px rhythm between every block on the page; no block
    // sets a margin of its own. The gutter itself belongs to `App`'s `<main>`.
    <div className={mergeClasses(layout.stack, responsive.touchTargets)}>
      <PageHeader
        title={mode === 'mine' ? 'My requests' : 'Dashboard'}
        description={
          mode === 'admin'
            ? 'Every request across the organisation, and who created and decided each one.'
            : mode === 'review'
              ? 'Requests waiting on your review, and what you have already decided.'
              : 'Everything you have raised, and where each one has got to.'
        }
        actions={
          // #243 — reviewers and admins never see a create-request affordance:
          // only a plain requester (neither role, mode === 'mine') may raise one.
          mode === 'mine' ? (
            <Button
              appearance="primary"
              onClick={() => {
                void navigate('/requests/new');
              }}
            >
              + New request
            </Button>
          ) : undefined
        }
      />

      {state.phase === 'loading' && <DashboardSkeleton />}

      {/* E3 — a load failure is never an empty state: "no requests yet" after a
          failed fetch tells a requester their data is gone. `ApiErrorMessage`
          owns this copy for the whole app, including the 401 (session expired,
          reload) and 403 (no access) branches this screen would otherwise have
          to duplicate. "Your requests are safe" is load-bearing copy. */}
      {state.phase === 'error' && (
        <ApiErrorMessage
          error={state.error}
          title={mode === 'mine' ? 'Could not load your requests' : 'Could not load the dashboard'}
          onRetry={() => void reload()}
        />
      )}

      {actionError && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Could not duplicate this request</MessageBarTitle>
            {actionError}
          </MessageBarBody>
          <MessageBarActions>
            <Button appearance="transparent" onClick={() => setActionError(null)}>
              Dismiss
            </Button>
          </MessageBarActions>
        </MessageBar>
      )}

      {/* R1a — never dead-end a new user: the empty state carries the way in and
          one sentence of what happens next. Counters and filters are hidden
          entirely (note 2), not shown as zeros. The action is `secondary`
          rather than `primary` because the page header's "+ New request" is
          already on screen, and exactly one filled button may be visible at a
          time; both do the same thing. */}
      {isFirstRun && (
        <EmptyState
          variant="first-run"
          title={
            mode === 'admin'
              ? 'No requests yet'
              : mode === 'review'
                ? 'Nothing to review yet'
                : 'No requests yet'
          }
          body={
            mode === 'admin'
              ? 'Nobody has raised a request yet.'
              : mode === 'review'
                ? 'Nothing is waiting on your review, and you have not decided on anything yet.'
                : 'Raise a purchase request and it will go to the review group. Once approved, a Purchase Requisition is created in D365 automatically.'
          }
          // #243 — reviewers and admins never see a create-request affordance.
          action={
            mode === 'mine' ? (
              <Button
                appearance="secondary"
                onClick={() => {
                  void navigate('/requests/new');
                }}
              >
                + New request
              </Button>
            ) : undefined
          }
        />
      )}

      {hasAnyRequestsEver && counts && (
        <>
          {/* Wireframe R1 — Total / Pending review / PR created / Rejected, and
              they are filters: selecting one applies that status, selecting it
              again clears it. `StatTile` carries the selected state as a
              border-WIDTH change plus `aria-pressed`, never as brand colour
              alone — high contrast discards the hue entirely. */}
          <fieldset className={mergeClasses(layout.tileRow, styles.tileGroup)}>
            <legend className={a11y.visuallyHidden}>Filter by status</legend>
            {(
              [
                { key: 'ALL' as const, label: 'Total', value: counts.total },
                {
                  key: 'PENDING_REVIEW' as const,
                  label: 'Pending review',
                  value: counts.pendingReview,
                },
                { key: 'PR_CREATED' as const, label: 'PR created', value: counts.prCreated },
                { key: 'REJECTED' as const, label: 'Rejected', value: counts.rejected },
              ] satisfies { key: RequestStatus | 'ALL'; label: string; value: number }[]
            ).map((tile) => (
              <StatTile
                key={tile.key}
                label={tile.label}
                value={tile.value}
                selected={
                  tile.key === 'ALL' ? filters.status.length === 0 : filters.status[0] === tile.key
                }
                onSelect={() => toggleCounter(tile.key)}
              />
            ))}
          </fieldset>

          {/* Wireframe R1c — search, status, company and date-range filters,
              plus the sort dropdown. The bar is rendered above the results and
              survives a search that matches nothing (E1 note 3), so there is
              always a way back. */}
          <FilterBar
            search={{
              value: searchInput,
              onChange: setSearchInput,
              placeholder: 'Request no, vendor or description',
              label: 'Search',
            }}
            activeCount={activeFilterCount}
            onClear={clearFilters}
          >
            <Field label="Status">
              <Dropdown
                multiselect
                placeholder="All"
                selectedOptions={filters.status}
                value={
                  filters.status.length === 0
                    ? ''
                    : filters.status.map((s) => STATUS_OPTION_LABELS[s]).join(', ')
                }
                onOptionSelect={(_, data) =>
                  updateFilters({ status: data.selectedOptions as RequestStatus[] })
                }
              >
                {STATUS_OPTIONS.map((s) => (
                  <Option key={s} value={s}>
                    {STATUS_OPTION_LABELS[s]}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            <Field label="Company">
              <Dropdown
                multiselect
                placeholder="All"
                selectedOptions={filters.company}
                value={filters.company.length === 0 ? '' : filters.company.join(', ')}
                onOptionSelect={(_, data) => updateFilters({ company: data.selectedOptions })}
              >
                {companies.map((c) => (
                  <Option key={c} value={c}>
                    {c}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            <Field label="Date range">
              <Dropdown
                selectedOptions={[filters.range]}
                value={RANGE_CHOICES.find((r) => r.value === filters.range)?.label ?? ''}
                onOptionSelect={(_, data) => {
                  if (data.optionValue) {
                    updateFilters({ range: data.optionValue as DashboardDateRange });
                  }
                }}
              >
                {RANGE_CHOICES.map((r) => (
                  <Option key={r.value} value={r.value}>
                    {r.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            {filters.range === 'custom' && (
              <>
                <Field label="From">
                  <DatePicker
                    placeholder="From"
                    value={filters.from ? fromDateOnly(filters.from) : null}
                    onSelectDate={(date) => updateFilters({ from: date ? toDateOnly(date) : '' })}
                  />
                </Field>
                <Field label="To">
                  <DatePicker
                    placeholder="To"
                    value={filters.to ? fromDateOnly(filters.to) : null}
                    onSelectDate={(date) => updateFilters({ to: date ? toDateOnly(date) : '' })}
                  />
                </Field>
              </>
            )}
            <Field label="Sort by">
              <Dropdown
                selectedOptions={[sortChoice]}
                value={SORT_CHOICES.find((s) => s.value === sortChoice)?.label ?? ''}
                onOptionSelect={(_, data) => {
                  const choice = data.optionValue as SortChoice | undefined;
                  if (!choice) return;
                  const [sort, order] = choice.split('-') as [
                    DashboardSortField,
                    DashboardSortOrder,
                  ];
                  updateFilters({ sort, order });
                }}
              >
                {SORT_CHOICES.map((s) => (
                  <Option key={s.value} value={s.value}>
                    {s.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          </FilterBar>
        </>
      )}

      {/* Wireframe R7a note 1 — only shown once more than one draft exists. */}
      {drafts.length > 1 && (
        <MessageBar intent="info">
          <MessageBarBody>
            <MessageBarTitle>You have {drafts.length} drafts in progress.</MessageBarTitle>
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Wireframe R7b — in-app only, one banner per draft close to expiry. */}
      {expiringDrafts.map((draft) => (
        <MessageBar key={draft.id} intent="warning">
          <MessageBarBody>
            <MessageBarTitle>
              This draft expires in {draft.daysUntilExpiry} day
              {draft.daysUntilExpiry === 1 ? '' : 's'}.
            </MessageBarTitle>
            Drafts are removed after 30 days of no activity. Submit it or edit it to reset the
            clock.
          </MessageBarBody>
          <MessageBarActions>
            <Button
              appearance="transparent"
              disabled={keepDraftBusyId === draft.id}
              onClick={() => void handleKeepDraft(draft.id)}
            >
              Keep draft
            </Button>
          </MessageBarActions>
        </MessageBar>
      ))}

      {/* E1 — distinct from R1a: the user HAS requests, this search/filter
          combination just doesn't match any of them. So the body names what was
          searched and says how many exist, and the way out is Clear filters —
          never "New request", which would answer a question nobody asked. */}
      {hasNoMatches && counts && (
        <EmptyState
          variant="no-results"
          title="No requests match your search or filters"
          body={`${
            filters.q === ''
              ? 'Nothing matches the filters you have applied.'
              : `Nothing matches “${filters.q}”.`
          } You have ${String(counts.total)} request${counts.total === 1 ? '' : 's'} in total.`}
          action={<Button onClick={clearFilters}>Clear filters</Button>}
        />
      )}

      {state.phase === 'ready' && !isFirstRun && !hasNoMatches && (
        // One column definition, two faces: a real table on desktop (wireframe
        // R1) and stacked cards below 700px (sheet 08/S1a), with a scroll
        // element inside the card so a column can never be silently clipped.
        <DataTable<RequestSummary>
          caption={mode === 'mine' ? 'My requests' : 'Dashboard'}
          // The heading IS the control that opens the request, rather than
          // plain text with the only way in buried further down the card.
          // Wireframe S1a: the card "leads with request number and status, the
          // two things being scanned" — and K1 requires a real focusable
          // control, not an onClick on the card itself.
          cardTitle={(request) => (
            <Link
              as="button"
              onClick={(event) => {
                event.stopPropagation();
                openRequest(request.id);
              }}
            >
              PR-{request.requestNo}
            </Link>
          )}
          columns={columns}
          rowKey={(request) => request.id}
          rows={requests}
          onRowClick={(request) => openRequest(request.id)}
          rowActions={renderRowActions}
          sort={{ key: filters.sort, order: filters.order }}
          onSort={handleSortClick}
        />
      )}

      {hasAnyRequestsEver && !isFirstRun && total > 0 && (
        <div className={surfaces.pager}>
          <Caption1>
            Showing {pageStart}–{pageEnd} of {total}
          </Caption1>
          <div className={styles.pagerButtons}>
            <Button
              disabled={filters.page <= 1}
              onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
            >
              Previous
            </Button>
            <Button
              disabled={pageEnd >= total}
              onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* D2 — delete requires naming what is lost; deleting is impossible without this. */}
      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(_, data) => {
          if (!data.open) setDeleteTarget(null);
        }}
      >
        {/* A dialog renders in a portal, outside the page's own subtree — it
            needs the touch-target rule applied to its surface directly. */}
        <DialogSurface className={responsive.touchTargets}>
          <DialogBody>
            <DialogTitle>Delete this draft?</DialogTitle>
            <DialogContent>
              {deleteTarget && (
                <>
                  PR-{deleteTarget.requestNo} “{deleteTarget.title}”, with{' '}
                  {deleteTarget._count.lines} line item{deleteTarget._count.lines === 1 ? '' : 's'},
                  will be permanently removed. This cannot be undone.
                </>
              )}
              {deleteError && (
                <Body1 as="p" className={styles.meta}>
                  {deleteError}
                </Body1>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                ref={keepDraftFocusRef}
                appearance="subtle"
                onClick={() => setDeleteTarget(null)}
              >
                Keep draft
              </Button>
              <Button
                appearance="secondary"
                className={styles.destructiveConfirm}
                onClick={() => void handleConfirmDelete()}
              >
                Delete draft
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* R1d/D2 — withdrawing requires naming what changes; it is not a delete,
          the request survives as an editable Draft, so it keeps the ordinary
          primary treatment (wireframe 10.1: "Withdraw is not destructive"). */}
      <Dialog
        open={withdrawTarget !== null}
        onOpenChange={(_, data) => {
          if (!data.open) setWithdrawTarget(null);
        }}
      >
        <DialogSurface className={responsive.touchTargets}>
          <DialogBody>
            <DialogTitle>Withdraw this request?</DialogTitle>
            <DialogContent>
              {withdrawTarget && (
                <>
                  PR-{withdrawTarget.requestNo} will be pulled out of review and returned to Draft.
                  It leaves the reviewer’s queue immediately — you can edit it and resubmit whenever
                  you’re ready.
                </>
              )}
              {withdrawError && (
                <Body1 as="p" className={styles.meta}>
                  {withdrawError}
                </Body1>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                ref={keepInReviewFocusRef}
                appearance="secondary"
                onClick={() => setWithdrawTarget(null)}
              >
                Keep in review
              </Button>
              <Button appearance="primary" onClick={() => void handleConfirmWithdraw()}>
                Withdraw
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
